import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Button, Notice, TextArea } from './primitives.js';

export interface ChatMessage {
  seq: number;
  mine: boolean;
  body: string;
  at: string;
}

export interface ChatThreadData {
  id: string;
  /** With a passenger (or driver), or between two drivers. */
  kind?: 'passenger' | 'friend';
  with: { name: string; phone: string | null };
  messages: ChatMessage[];
  canSend: boolean;
  why: string | null;
  closed: boolean;
  blockedByMe: boolean;
  request: { sent: number; max: number } | null;
}

/** The calls a conversation screen needs; each app passes its own signed-in client. Keep it stable. */
export interface ChatApi {
  thread: (id: string, after: number) => Promise<ChatThreadData>;
  send: (id: string, body: string) => Promise<ChatMessage>;
  read: (id: string) => Promise<unknown>;
  block: (id: string, on: boolean) => Promise<ChatThreadData>;
  report: (id: string, reason: string) => Promise<unknown>;
}

const merge = (old: ChatMessage[], fresh: ChatMessage[]) => {
  const seen = new Set(old.map((m) => m.seq));
  return [...old, ...fresh.filter((m) => !seen.has(m.seq))].sort((a, b) => a.seq - b.seq);
};

/** Loads a conversation, asks for new messages every few seconds while it is on screen, and marks it read. */
export function useConversation(id: string, api: ChatApi, onRead?: () => void, pollMs = 4000) {
  const [data, setData] = useState<ChatThreadData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const last = useRef(0);

  const load = useCallback(
    async (fresh = false) => {
      try {
        const t = await api.thread(id, fresh ? 0 : last.current);
        setData((prev) => {
          const messages = fresh || !prev ? t.messages : merge(prev.messages, t.messages);
          last.current = messages.at(-1)?.seq ?? 0;
          return { ...t, messages };
        });
        if ((fresh || t.messages.some((m) => !m.mine)) && document.visibilityState === 'visible')
          await api.read(id).then(() => onRead?.(), () => undefined);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load the messages.');
      }
    },
    [id, api, onRead],
  );

  useEffect(() => {
    last.current = 0;
    void load(true);
    const tick = () => {
      if (document.visibilityState === 'visible') void load();
    };
    const timer = setInterval(tick, pollMs);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load, pollMs]);

  const send = async (body: string) => {
    const m = await api.send(id, body);
    setData((d) => d && { ...d, messages: merge(d.messages, [m]) });
    await load();
  };
  const block = async (on: boolean) => setData(await api.block(id, on));
  return { data, error, send, block, report: (reason: string) => api.report(id, reason) };
}

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
const stamp = (iso: string) => {
  const d = new Date(iso);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return sameDay(d, new Date()) ? time : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
};

/** A conversation: a call button, the messages, and a box to write in. */
export function ChatThread({ convo, phoneLabel }: { convo: ReturnType<typeof useConversation>; phoneLabel?: string }) {
  const { data, error } = convo;
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [panel, setPanel] = useState<'none' | 'more' | 'report' | 'reported'>('none');
  const [reason, setReason] = useState('');
  const end = useRef<HTMLDivElement>(null);
  const count = data?.messages.length ?? 0;
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [count]);

  if (!data) return error ? <Notice tone="danger">{error}</Notice> : <p className="text-sm text-muted">Loading…</p>;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    setProblem(null);
    try {
      await convo.send(body);
      setText('');
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not send. Try again.');
    } finally {
      setBusy(false);
    }
  };
  const act = async (fn: () => Promise<unknown>, after: typeof panel) => {
    setProblem(null);
    try {
      await fn();
      setPanel(after);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'That did not work.');
    }
  };
  const left = data.request ? data.request.max - data.request.sent : null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h1 className="min-w-0 flex-1 truncate text-xl font-black">{data.with.name}</h1>
        {data.with.phone && (
          <a href={`tel:${data.with.phone}`} className="rounded-xl bg-card2 px-3 py-2 text-sm font-bold" aria-label={`Call ${data.with.name}${phoneLabel ? ` at ${phoneLabel}` : ''}`}>
            Call
          </a>
        )}
        <button type="button" onClick={() => setPanel(panel === 'more' ? 'none' : 'more')} aria-expanded={panel === 'more'} className="rounded-xl bg-card2 px-3 py-2 text-sm font-bold">
          More
        </button>
      </div>

      {panel === 'more' && (
        <div className="flex flex-wrap gap-2 rounded-2xl border border-line bg-card p-3">
          {data.blockedByMe ? (
            <Button small variant="ghost" onClick={() => void act(() => convo.block(false), 'none')}>Turn messages back on</Button>
          ) : (
            !data.closed && <Button small variant="ghost" onClick={() => void act(() => convo.block(true), 'none')}>Turn off messages from {data.with.name}</Button>
          )}
          <Button small variant="danger" onClick={() => setPanel('report')}>Report this conversation</Button>
        </div>
      )}
      {panel === 'report' && (
        <div className="space-y-2 rounded-2xl border border-line bg-card p-3">
          <label htmlFor="report-reason" className="block text-sm font-bold">What is wrong? (optional)</label>
          <TextArea id="report-reason" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
          <p className="text-xs text-muted">RydRes keeps a copy of this conversation to look into it.</p>
          <div className="flex gap-2">
            <Button small variant="danger" onClick={() => void act(() => convo.report(reason.trim()), 'reported')}>Send report</Button>
            <Button small variant="ghost" onClick={() => setPanel('none')}>Cancel</Button>
          </div>
        </div>
      )}
      {panel === 'reported' && <Notice tone="ok">Reported. RydRes keeps a copy of this conversation to look into it.</Notice>}

      <ol className="space-y-2" aria-label="Messages" aria-live="polite">
        {data.messages.length === 0 && (
          <li className="py-6 text-center text-sm text-muted">{data.kind === 'friend' ? 'No messages yet. Say hello.' : 'No messages yet. Say hello, or ask about your ride.'}</li>
        )}
        {data.messages.map((m) => (
          <li key={m.seq} className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 ${m.mine ? 'bg-accent text-on-accent' : 'border border-line bg-card text-ink'}`}>
              <span className="sr-only">{m.mine ? 'You' : data.with.name}: </span>
              <p className="whitespace-pre-wrap break-words text-[15px]">{m.body}</p>
              <p className={`mt-0.5 text-right text-[11px] ${m.mine ? '' : 'text-muted'}`}>{stamp(m.at)}</p>
            </div>
          </li>
        ))}
      </ol>
      <div ref={end} />

      {data.canSend ? (
        <form onSubmit={(e) => void submit(e)} className="space-y-2">
          <label htmlFor="message" className="sr-only">Message to {data.with.name}</label>
          <TextArea id="message" value={text} maxLength={2000} rows={2} onChange={(e) => setText(e.target.value)} placeholder={`Message ${data.with.name}`} />
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted">{left !== null ? `${left} of ${data.request!.max} messages left until ${data.with.name} replies.` : 'For anything urgent on the day, call.'}</p>
            <Button type="submit" small disabled={busy || !text.trim()}>{busy ? 'Sending…' : 'Send'}</Button>
          </div>
        </form>
      ) : (
        <Notice tone="info">{data.why}</Notice>
      )}
      {(problem || (error && data)) && <Notice tone="danger">{problem ?? error}</Notice>}
    </div>
  );
}

const WIDE = '(min-width: 1024px)';

/** Whether the screen has room for a conversation beside the page. */
function useWide() {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE).matches);
  useEffect(() => {
    const mq = window.matchMedia(WIDE);
    const change = () => setWide(mq.matches);
    mq.addEventListener('change', change);
    return () => mq.removeEventListener('change', change);
  }, []);
  return wide;
}

/**
 * A conversation over the screen that opened it: the whole screen on a phone, a 440 px panel on
 * the right of a wide screen (give the page `lg:pr-[440px]` meanwhile). The button or Escape closes it.
 */
export function ChatSheet({ convo, onClose }: { convo: ReturnType<typeof useConversation>; onClose: () => void }) {
  const wide = useWide();
  const closeButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeButton.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  // The page behind a full-screen conversation stays put.
  useEffect(() => {
    if (wide) return;
    const before = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = before;
    };
  }, [wide]);
  return (
    <section
      role="dialog"
      aria-modal={wide ? undefined : true}
      aria-label={convo.data ? `Conversation with ${convo.data.with.name}` : 'Conversation'}
      className="fixed inset-0 z-30 flex flex-col bg-ground lg:left-auto lg:w-[440px] lg:border-l lg:border-line lg:shadow-xl"
    >
      <div className="flex items-center gap-2 border-b border-line bg-card px-3 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))]">
        <button ref={closeButton} type="button" onClick={onClose} className="min-h-11 min-w-11 rounded-full bg-card2 px-3 font-bold" aria-label="Close the conversation">
          {wide ? '✕' : '‹'}
        </button>
        <span className="text-sm font-bold text-muted">Messages</span>
      </div>
      <div className="flex-1 overflow-y-auto px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">
        <ChatThread convo={convo} />
      </div>
    </section>
  );
}
